<template>
  <div class="checkout-flow-page">
    <div class="checkout-container">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ statusMessage }}</p>
      </div>

      <div v-else-if="error" class="error-state">
        <svg class="error-icon" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd"
          />
        </svg>
        <h2>Checkout Failed</h2>
        <p class="error-message">{{ error }}</p>
        <button @click="retry" class="retry-button">Try Again</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { fetchWithAuth } from '../services/api';
import { getGaIds } from '../lib/analytics';
import { getAttribution, getTouches } from '../lib/attribution';
import { getRewardfulReferral } from '../lib/rewardful';

const route = useRoute();
const router = useRouter();

const projectId = route.params['projectId'] as string;
const loading = ref(true);
const error = ref('');
const statusMessage = ref('Preparing checkout...');

async function initiateCheckout() {
  loading.value = true;
  error.value = '';

  try {
    statusMessage.value = 'Creating checkout session...';

    // Get query params for promo code, referral, credit usage, etc.
    const promoCode = route.query['code'] as string;
    const test = route.query['test'] === 'true';
    const useCredit = route.query['useCredit'] === 'true';
    const referral = await getRewardfulReferral();
    const cancelUrl = document.referrer || undefined;
    const { clientId: gaClientId, sessionId: gaSessionId } = getGaIds();

    // Build URL with query parameters
    const url = new URL(`/api/projects/${projectId}/checkout`, window.location.origin);
    if (useCredit) {
      url.searchParams.set('useCredit', 'true');
    }

    const response = await fetchWithAuth(url.pathname + url.search, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        promoCode,
        test,
        referral,
        cancelUrl,
        gaClientId,
        gaSessionId,
        attribution: getAttribution(),
        touches: getTouches(),
      }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to create checkout session');
    }

    const data = await response.json();

    // Redirect to Stripe (external) or in-app route
    if (data.redirectUrl.startsWith('http://') || data.redirectUrl.startsWith('https://')) {
      // External URL (Stripe checkout) - use full page redirect
      window.location.href = data.redirectUrl;
    } else {
      // Internal route - use router to stay in SPA
      router.replace(data.redirectUrl);
    }
  } catch (err: any) {
    console.error('Checkout flow error:', err);
    error.value = err.message || 'An unexpected error occurred';
    loading.value = false;
  }
}

function retry() {
  error.value = '';
  initiateCheckout();
}

onMounted(() => {
  if (!projectId) {
    error.value = 'No project ID provided';
    loading.value = false;
    return;
  }

  initiateCheckout();
});
</script>

<style scoped>
.checkout-flow-page {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(to bottom, var(--color-bg-primary), var(--color-bg-secondary));
  padding: 2rem;
}

.checkout-container {
  background: transparent;
  border-radius: 0;
  padding: 0;
  max-width: 500px;
  width: 100%;
  box-shadow: none;
  text-align: center;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  color: #4a5568;
  font-size: 1rem;
  margin: 0;
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.error-icon {
  width: 64px;
  height: 64px;
  color: #fc8181;
}

.error-state h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.error-message {
  color: #e53e3e;
  font-size: 1rem;
  margin: 0;
  line-height: 1.5;
}

.retry-button {
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  background: var(--color-accent);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.retry-button:hover {
  background: var(--color-accent-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(97, 174, 212, 0.3);
}
</style>
